import { type KeyboardEvent, useRef } from 'react'

export interface ProductTabItem<T extends string> {
  value: T
  label: string
}

interface ProductTabsProps<T extends string> {
  tabs: ProductTabItem<T>[]
  active: T
  onSelect: (tab: T) => void
  /** Out of the tab order, while the strip is hidden on the collapsed rail. */
  disabled: boolean
}

const STEP: Partial<Record<string, (index: number, count: number) => number>> = {
  ArrowRight: (index, count) => (index + 1) % count,
  ArrowLeft: (index, count) => (index - 1 + count) % count,
  Home: () => 0,
  End: (_, count) => count - 1,
}

/** The product switch. Wraps to a second row rather than hiding a tab off the edge of the rail. */
export function ProductTabs<T extends string>({ tabs, active, onSelect, disabled }: ProductTabsProps<T>) {
  const buttons = useRef<(HTMLButtonElement | null)[]>([])

  // The arrows move between tabs and select as they go, like any tablist.
  const onKeyDown = (event: KeyboardEvent, index: number) => {
    const step = STEP[event.key]
    if (!step) return
    event.preventDefault()
    const next = step(index, tabs.length)
    onSelect(tabs[next].value)
    buttons.current[next]?.focus()
  }

  return (
    <div
      role="tablist"
      aria-label="Cai products"
      className="border-ink-50 inline-flex h-auto w-full max-w-full flex-wrap items-center justify-start gap-0.5 rounded-xl border bg-transparent p-1"
    >
      {tabs.map((tab, index) => (
        <button
          key={tab.value}
          ref={(button) => {
            buttons.current[index] = button
          }}
          type="button"
          role="tab"
          aria-selected={tab.value === active}
          tabIndex={!disabled && tab.value === active ? 0 : -1}
          onClick={() => onSelect(tab.value)}
          onKeyDown={(event) => onKeyDown(event, index)}
          className="text-ink-900/60 hover:text-ink-900 focus-visible:ring-brand-700/50 aria-selected:bg-brand-100 aria-selected:text-ink relative inline-flex h-7 flex-auto cursor-pointer items-center justify-center gap-2 rounded-lg px-2 py-1 text-xs font-medium whitespace-nowrap transition-all outline-none focus-visible:ring-[3px] aria-selected:shadow-sm motion-reduce:transition-none"
        >
          {tab.label}
        </button>
      ))}
    </div>
  )
}
